import { safeBig } from 'utils/FormatNumber';
import Big from 'big.js';
import { TokenStatsWebSocketShortMessage } from 'api/hooks/useTokenStats';
import { HotTokensRowInfo } from 'api/types/httpsTypes/contracts';

export interface MappedHotTokenStats {
  suspiciousBuy: Big;
  rugAddictedBuy: Big;
  scammerBuy: Big;
  freshBuy: Big;

  totalBuy: Big;
  totalBuyTop: Big;

  totalSells: Big;
  totalSellsTop: Big;

  netInflow: Big;
  netInflowTop: Big;

  buyVolume: Big;
  buyVolumeTop: Big;

  sellVolume: Big;
  sellVolumeTop: Big;

  volatilityIndex: Big;

  suspiciousRatio: Big;
  freshRatio: Big;
  buySellRatio: Big;

  marketCap: Big;
  price: Big;
  priceChange: Big;

  status: string;
  volume: Big;
  liquidity: Big;
}

export function hotTokenDataMapping(
  tokenStats: TokenStatsWebSocketShortMessage | null,
  stats: HotTokensRowInfo
): MappedHotTokenStats {
  const marketCap = tokenStats?.m
    ? safeBig(tokenStats.m)
    : safeBig(stats?.currentMarketCap);

  const liquidity = tokenStats?.l
    ? safeBig(tokenStats.l)
    : safeBig(stats?.currentLiquidity);
  const price = tokenStats?.p
    ? safeBig(tokenStats.p)
    : safeBig(stats?.currentPrice);
  const priceChange = tokenStats?.pc
    ? safeBig(tokenStats.pc)
    : safeBig(stats?.priceChange);
  const status = tokenStats?.s ? tokenStats.s : stats?.status ?? '';
  const volume = tokenStats?.v ? safeBig(tokenStats.v) : safeBig(stats?.volume);

  const suspiciousBuy = tokenStats?.swb
    ? safeBig(tokenStats.swb)
    : safeBig(stats?.suspiciousBuy);
  const rugAddictedBuy = tokenStats?.rwb
    ? safeBig(tokenStats.rwb)
    : safeBig(stats?.rugAddictedBuy);
  const scammerBuy = tokenStats?.scb
    ? safeBig(tokenStats.scb)
    : safeBig(stats?.scammerBuy);
  const freshBuy = tokenStats?.fwb
    ? safeBig(tokenStats.fwb)
    : safeBig(stats?.freshBuy);

  const totalBuy = tokenStats?.twb
    ? safeBig(tokenStats.twb)
    : safeBig(stats?.totalBuy);
  const totalBuyTop = tokenStats?.twbt
    ? safeBig(tokenStats.twbt)
    : safeBig(stats?.totalBuyTop);

  const totalSells = tokenStats?.tws
    ? safeBig(tokenStats.tws)
    : safeBig(stats?.totalSells);
  const totalSellsTop = tokenStats?.twst
    ? safeBig(tokenStats.twst)
    : safeBig(stats?.totalSellsTop);

  const netInflow = tokenStats?.ni
    ? safeBig(tokenStats.ni)
    : safeBig(stats?.netInflow);
  const netInflowTop = tokenStats?.nit
    ? safeBig(tokenStats.nit)
    : safeBig(stats?.netInflowTop);

  const buyVolume = tokenStats?.bv
    ? safeBig(tokenStats.bv)
    : safeBig(stats?.buyVolume);
  const buyVolumeTop = tokenStats?.bvt
    ? safeBig(tokenStats.bvt)
    : safeBig(stats?.buyVolumeTop);

  const sellVolume = tokenStats?.sv
    ? safeBig(tokenStats.sv)
    : safeBig(stats?.sellVolume);
  const sellVolumeTop = tokenStats?.svt
    ? safeBig(tokenStats.svt)
    : safeBig(stats?.sellVolumeTop);

  const volatilityIndex = tokenStats?.vi
    ? safeBig(tokenStats.vi)
    : safeBig(stats?.volatilityIndex);

  const suspiciousRatio = totalBuy.gt(0)
    ? suspiciousBuy.plus(rugAddictedBuy).plus(scammerBuy).div(totalBuy).mul(100)
    : Big(0);

  const freshRatio = totalBuy.gt(0) ? freshBuy.div(totalBuy).mul(100) : Big(0);

  const buySellRatio = totalSells.gt(0)
    ? totalBuy.div(totalSells).mul(100)
    : Big(0);

  return {
    suspiciousBuy,
    rugAddictedBuy,
    scammerBuy,
    freshBuy,

    totalBuy,
    totalBuyTop,

    totalSells,
    totalSellsTop,

    netInflow,
    netInflowTop,

    buyVolume,
    buyVolumeTop,

    sellVolume,
    sellVolumeTop,

    volatilityIndex,

    suspiciousRatio,
    freshRatio,
    buySellRatio,

    marketCap,
    price,
    priceChange,
    status,
    volume,
    liquidity,
  };
}
